import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import { CheckIcon, ChevronDownIcon } from './icons'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps {
  /** Pair with a `<label htmlFor={id}>`, which names the trigger. */
  id: string
  /** Names the open list; kept distinct from the label so the trigger stays the only match for it. */
  listLabel: string
  options: SelectOption[]
  /** `''` shows the placeholder. */
  value: string
  /** Also listed first in the open list, where picking it reports `''`. */
  placeholder: string
  onChange: (value: string) => void
  disabled?: boolean
  invalid?: boolean
  'aria-describedby'?: string
}

const TYPEAHEAD_RESET_MS = 500

/**
 * A single-choice dropdown following the WAI-ARIA select-only combobox pattern: a
 * button trigger owns a listbox it opens below itself. Focus stays on the trigger and
 * the highlighted option is tracked with aria-activedescendant, so a click outside (a
 * blur) is all it takes to close it.
 */
export function Select({
  id,
  listLabel,
  options: choices,
  value,
  placeholder,
  onChange,
  disabled,
  invalid,
  'aria-describedby': describedBy,
}: SelectProps) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const typeahead = useRef({ text: '', at: 0 })
  // Hovering highlights too, but scrolling under the pointer would move the list beneath it.
  const scrollToActive = useRef(false)
  const listId = `${id}-listbox`
  const optionId = (index: number) => `${id}-option-${index}`
  const options: SelectOption[] = [{ value: '', label: placeholder }, ...choices]
  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = options[selectedIndex]
  const expanded = open && !disabled

  useEffect(() => {
    if (!expanded || active < 0 || !scrollToActive.current) return
    // jsdom does not implement scrolling, hence the optional call.
    document.getElementById(`${id}-option-${active}`)?.scrollIntoView?.({ block: 'nearest' })
  }, [expanded, active, id])

  const enabled = (index: number) => options[index] !== undefined && !options[index]!.disabled

  function step(from: number, delta: 1 | -1): number {
    for (let i = from + delta; i >= 0 && i < options.length; i += delta) {
      if (enabled(i)) return i
    }
    return from
  }
  const first = () => step(-1, 1)
  const last = () => step(options.length, -1)

  function openAt(index: number) {
    scrollToActive.current = true
    setActive(index)
    setOpen(true)
  }

  function commit(index: number) {
    // Like a native select, re-picking the current value is not a change.
    if (enabled(index) && options[index]!.value !== value) onChange(options[index]!.value)
    setOpen(false)
  }

  function matchTypeahead(char: string): number {
    const now = Date.now()
    const state = typeahead.current
    state.text = now - state.at > TYPEAHEAD_RESET_MS ? char : state.text + char
    state.at = now
    const query = state.text.toLowerCase()
    const start = expanded ? active : selectedIndex
    for (let offset = 1; offset <= options.length; offset++) {
      const i = (start + offset + options.length) % options.length
      if (i > 0 && enabled(i) && options[i]!.label.toLowerCase().startsWith(query)) return i
    }
    return -1
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    scrollToActive.current = true
    const { key } = event
    if (key.length === 1 && key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const match = matchTypeahead(key)
      if (match >= 0) openAt(match)
      return
    }
    if (!expanded) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key)) {
        event.preventDefault()
        openAt(selectedIndex >= 0 ? selectedIndex : key === 'ArrowUp' ? last() : first())
      } else if (key === 'Home' || key === 'End') {
        event.preventDefault()
        openAt(key === 'Home' ? first() : last())
      }
      return
    }
    switch (key) {
      case 'ArrowDown':
        event.preventDefault()
        if (event.altKey) return
        setActive((i) => step(i, 1))
        return
      case 'ArrowUp':
        event.preventDefault()
        if (event.altKey) commit(active)
        else setActive((i) => step(i, -1))
        return
      case 'Home':
        event.preventDefault()
        setActive(first())
        return
      case 'End':
        event.preventDefault()
        setActive(last())
        return
      case 'Enter':
      case ' ':
        event.preventDefault()
        commit(active)
        return
      case 'Escape':
        event.preventDefault()
        setOpen(false)
        return
      case 'Tab':
        setOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() =>
          expanded ? setOpen(false) : openAt(selectedIndex >= 0 ? selectedIndex : first())
        }
        onKeyDown={handleKeyDown}
        onBlur={() => setOpen(false)}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-button border bg-surface px-3 py-2 text-left text-sm text-body',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
          'disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-danger' : expanded ? 'border-primary' : 'border-input',
        )}
      >
        <span className={cn('truncate', (!selected || value === '') && 'text-muted')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDownIcon
          className={cn('shrink-0 text-subtle transition-transform', expanded && 'rotate-180')}
        />
      </button>
      {expanded && (
        <ul
          id={listId}
          role="listbox"
          aria-label={listLabel}
          className="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-surface p-1.5 shadow-panel"
        >
          {options.map((option, index) => {
            const isSelected = index === selectedIndex
            const isPlaceholder = index === 0
            return (
              <li
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                // Keeps focus on the trigger so its blur doesn't close the list first.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => {
                  if (!enabled(index)) return
                  scrollToActive.current = false
                  setActive(index)
                }}
                onClick={() => enabled(index) && commit(index)}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-md px-3 py-2.5 text-sm text-body',
                  index === active && 'bg-surface-hover',
                  isPlaceholder ? 'text-muted' : isSelected && 'font-semibold',
                  option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                )}
              >
                <span>{option.label}</span>
                {isSelected && !isPlaceholder && (
                  <CheckIcon
                    className={cn(
                      'h-4 w-4 shrink-0',
                      // success falls below 3:1 on surface-hover.
                      index === active ? 'text-body' : 'text-success',
                    )}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
